// AssoLayout.jsx — Layout staff (sidebar éditoriale, white-label par asso)
// La couleur dynamique vient de l'asso via --theme-primary
import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { applyTheme } from '@/lib/theme';
import { usePermissions } from '@/lib/usePermissions';
import {
  LayoutDashboard, Users, Calendar, Handshake, Settings, LogOut, Loader2,
  ChevronRight, ExternalLink, Crown, Coins, Building2, MessageCircle, Newspaper,
  CalendarDays, Sparkles, Plug, BadgePercent, Zap, BarChart3, Globe, UserCheck, Receipt, UserMinus, Dumbbell, HeartHandshake,
} from 'lucide-react';
import useUnreadNotifications from '@/lib/useUnreadNotifications';

export default function AssoLayout() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [asso, setAsso] = useState(null);
  const [loading, setLoading] = useState(true);
  const { can, role: userRole } = usePermissions();
  const { unread } = useUnreadNotifications({ enabled: can('messages', 'view') });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/assos/me');
        setAsso(data);
        if (data?.couleur_principale) {
          applyTheme(data.couleur_principale);
          const merged = { ...user, couleur_principale: data.couleur_principale, asso_nom: data.nom };
          localStorage.setItem('assoperf_user', JSON.stringify(merged));
          setUser(merged);
        }
      } catch (e) {} finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
      </div>
    );
  }

  const initials = (asso?.nom || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const navItems = [
    { to: '/dashboard', label: 'Vue d\'ensemble', icon: LayoutDashboard, end: true, show: true },
    { to: '/dashboard/kpi', label: 'Tableau de bord', icon: BarChart3, show: can('compta', 'view') || userRole === 'super_admin' || !userRole },
    { to: '/dashboard/membres', label: 'Membres', icon: Users, show: can('membres', 'list') },
    { to: '/dashboard/adhesions', label: 'Adhésions', icon: UserCheck, show: can('membres', 'list') },
    { to: '/dashboard/groupes', label: 'Groupes', icon: Dumbbell, show: asso?.type === 'sport' && can('membres', 'list') },
    { to: '/dashboard/benevoles', label: 'Bénévoles', icon: HeartHandshake, show: can('evenements', 'list') },
    { to: '/dashboard/formules', label: 'Formules', icon: BadgePercent, show: can('config', 'update') },
    { to: '/dashboard/bureau', label: 'Bureau', icon: Crown, show: can('bureau', 'list') },
    { to: '/dashboard/demandes-depart', label: 'Demandes de départ', icon: UserMinus, show: userRole === 'president' },
    { to: '/dashboard/tresorerie', label: 'Trésorerie', icon: Coins, show: can('compta', 'view') },
    { to: '/dashboard/dons', label: 'Dons & reçus', icon: Receipt, show: can('compta', 'view') },
    { to: '/dashboard/annuaire', label: 'Annuaire', icon: Building2, show: can('annuaire', 'view') },
    { to: '/dashboard/messages', label: 'Messages', icon: MessageCircle, show: can('messages', 'view'), badge: unread },
    { to: '/dashboard/gazette', label: 'Gazette', icon: Newspaper, show: can('gazette', 'view') },
    { to: '/dashboard/agenda-pro', label: 'Agenda Pro', icon: CalendarDays, show: can('annuaire', 'view') },
    { to: '/dashboard/relances', label: 'Relances IA', icon: Sparkles, show: can('membres', 'list') },
    { to: '/dashboard/evenements', label: 'Événements', icon: Calendar, show: can('evenements', 'list') || userRole === 'super_admin' || !userRole },
    { to: '/dashboard/speed-business', label: 'Speed Business', icon: Zap, show: can('evenements', 'list') || userRole === 'super_admin' || !userRole },
    { to: '/dashboard/sponsors', label: 'Sponsors', icon: Handshake, show: can('sponsors', 'list') },
    { to: '/dashboard/integrations', label: 'Intégrations', icon: Plug, show: userRole === 'president' },
    { to: '/dashboard/config/vitrine', label: 'Vitrine publique', icon: Globe, show: can('config', 'update') || userRole === 'super_admin' || !userRole },
    { to: '/dashboard/parametres', label: 'Paramètres', icon: Settings, show: can('config', 'view') || userRole === 'super_admin' || !userRole },
  ].filter((i) => i.show);

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased flex" data-testid="asso-layout">

      {/* ─── Sidebar desktop ─── */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E2E8F0] flex-col sticky top-0 h-screen">
        {/* En-tête asso */}
        <div className="p-5 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            {asso?.logo_url ? (
              <img src={asso.logo_url} alt={asso.nom} className="w-10 h-10 rounded-xl object-cover shadow-sm" />
            ) : (
              <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black text-sm shadow-sm" style={{ background: 'var(--theme-primary)' }}>
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-[#0F172A] truncate text-sm leading-tight">{asso?.nom}</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#94A3B8] font-bold mt-1">Administration</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.to.split('/').pop() || 'dashboard'}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive
                    ? 'text-[var(--theme-primary)] bg-[var(--theme-primary-soft)] font-semibold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] font-medium'
                }`
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge > 0 && (
                <span className="text-[9px] font-black text-white px-1.5 py-0.5 rounded-full leading-none" style={{ background: 'var(--theme-primary)' }}>
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-[#E2E8F0] space-y-0.5">
          <Link
            to="/espace"
            className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors"
            data-testid="nav-member-space"
          >
            <span className="flex items-center gap-3">
              <ExternalLink className="w-4 h-4" />
              Espace membre
            </span>
            <ChevronRight className="w-3 h-3" />
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors"
            data-testid="asso-logout"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
          <div className="pt-3 px-3 border-t border-[#E2E8F0] mt-2">
            <div className="text-xs text-[#0F172A] font-semibold truncate">{user?.prenom} {user?.nom}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94A3B8] capitalize font-bold mt-0.5">{user?.role?.replace('_', ' ')}</div>
          </div>
        </div>
      </aside>

      {/* ─── Topbar mobile ─── */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg grid place-items-center text-white font-display font-black text-xs shrink-0" style={{ background: 'var(--theme-primary)' }}>
              {initials}
            </div>
            <span className="font-display font-bold text-[#0F172A] text-sm truncate">{asso?.nom}</span>
          </div>
          <button onClick={logout} className="btn-ghost !p-2" data-testid="asso-logout-mobile">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="flex overflow-x-auto px-2 pb-2 gap-1.5 scrollbar-thin">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-white'
                    : 'text-[#475569] bg-[#F1F5F9] hover:bg-[#E2E8F0]'
                }`
              }
              style={({ isActive }) => isActive ? { background: 'var(--theme-primary)' } : {}}
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
              {item.badge > 0 && (
                <span className="text-[9px] font-black text-white px-1 rounded-full leading-none ml-1" style={{ background: 'var(--theme-primary)' }}>
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>

      {/* ─── Contenu ─── */}
      <main className="flex-1 min-w-0 lg:pt-0 pt-24">
        <Outlet context={{ asso, setAsso }} />
      </main>
    </div>
  );
}
